<template>
  <div class="rental-intake-item-model-edit-form flex flex-col">
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <div>
        <label>
          <span class="sr-only block text-sm">Artikelnaam</span>
          <InputText
            v-model="rentalIntakeItem.name"
            ref="inputName"
            placeholder="Artikelnaam"
            :errors="v$.name.$errors"
            @focusout="v$.name.$touch()"
          />
        </label>
      </div>

      <div>
        <label>
          <span class="sr-only block text-sm">Eenheid</span>
          <UnitCombobox
            v-model="rentalIntakeItem.unit"
            input-placeholder="Eenheid"
            grow
            :errors="v$.unit.$errors"
            @focusout="v$.unit.$touch()"
            :disabled="true"
          />
        </label>
      </div>

      <div>
        <label>
          <span class="sr-only block text-sm">Leverancier</span>
          <KnownBusinessEntityCombobox
            v-model="rentalIntakeItem.supplier"
            :items="knownBusinessEntities"
            items-key-name="id"
            items-value-name="name"
            input-placeholder="Leverancier"
            creation-option
            grow
            auto-select-newly-created-model
            :creation-model-type="KnownBusinessEntity"
            :errors="v$.supplier.$errors"
            @focusout="v$.supplier.$touch()"
          />
        </label>
      </div>

      <ModelEditFormProjectRentalIntakeItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :rental-intake-item="rentalIntakeItem"
        ref="modelEditFormProjectRentalIntakeItem"
      />

      <div>
        <label>
          <span class="block text-sm">Eenheidsprijs per dag</span>
          <InputNumber
            v-model="rentalIntakeItem.unitPricePerDay"
            placeholder="Eenheidsprijs per dag"
            allow-zero
          >
            <span class="sm:text-sm">&euro;</span>
          </InputNumber>
        </label>
      </div>

      <div>
        <label>
          <span class="sr-only block text-sm">Corresponderend verhuurartikel</span>
          <InputCombobox
            v-model="rentalIntakeItem.rentalDispenseItem"
            :items="rentalDispenseItems"
            items-key-name="id"
            :items-value-function="(rii: RentalIntakeItem) => `${rii.name} ${rii.getUnitName()}`"
            input-placeholder="Corresponderend verhuurartikel"
            creation-option
            grow
            auto-select-newly-created-model
            :creation-model-type="RentalDispenseItem"
            :errors="v$.rentalDispenseItem.$errors"
            @focusout="v$.rentalDispenseItem.$touch()"
            :creation-parameters="rentalDispenseItemModelFormCreationParameters"
          />
        </label>
      </div>
    </div>
    <FormValidationAlert v-model="v$" />
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from "vue"
import InputText from "@/components/inputs/InputText.vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import InputNumber from "@/components/inputs/InputNumber.vue"
import { RentalIntakeItem } from "@/models/rental-intake-item.model"
import { storeToRefs } from "pinia"
import { useRentalDispenseItemStore } from "@/store/rental-dispense-item.store"
import { RentalDispenseItem } from "@/models/rental-dispense-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import type { RentalIntakeItemController } from "@/controllers/model-controllers/rental-intake-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import UnitCombobox from "@/components/inputs/UnitCombobox.vue"
import { upperFirst } from "lodash-es"
import { Units } from "@/enums/units.enum"
import { Project } from "@/models/project.model"
import ModelEditFormProjectRentalIntakeItem from "@/components/administrative_item/ModelEditFormProjectRentalIntakeItem.vue"
import { RentalIntake } from "@/models/rental-intake.model"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"
import { uniqueRentalIntakeItemValidator } from "@/helper/validation/rental-intake-item-validation.ts"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store.ts"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import KnownBusinessEntityCombobox from "@/components/known_business_entity/KnownBusinessEntityCombobox.vue"

const inputName = ref()

const knownBusinessEntityStore = useKnownBusinessEntityStore()
const rentalDispenseItemStore = useRentalDispenseItemStore()
const { knownBusinessEntities } = storeToRefs(knownBusinessEntityStore)
const { rentalDispenseItems } = storeToRefs(rentalDispenseItemStore)
const rules = {
  supplier: { requiredValidator },
  name: { requiredValidator },
  unit: { requiredValidator },
  rentalDispenseItem: { requiredValidator },
  $validator: uniqueRentalIntakeItemValidator
}

const controller: RentalIntakeItemController = inject("rentalIntakeItemController")!

const {
  model: rentalIntakeItem,
  initialText,
  v$
} = useFormConfigurator<RentalIntakeItem>({ controller, rules, customSaveHandler })

const creationParameters: { project?: Project; administrativeEvent?: RentalIntake } =
  inject("creationParameters")!
let rentalDispenseItemModelFormCreationParameters = creationParameters
if (creationParameters.administrativeEvent) {
  // In case of editing a rental intake item based on a rental intake processing item, pass down project information of
  // the associated rental dispense.
  rentalDispenseItemModelFormCreationParameters = {
    project: creationParameters.administrativeEvent.associatedRentalDispense?.project
  }
}

onMounted(() => {
  if (initialText) {
    rentalIntakeItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, rentalIntakeItem, "name")

  rentalIntakeItem.value.unit = Units.PIECE
})

const modelEditFormProjectRentalIntakeItem = ref()
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project && modelEditFormProjectRentalIntakeItem.value) {
    await modelEditFormProjectRentalIntakeItem.value.savePriceAgreement()
  }
}
</script>

<style scoped></style>
